import { useAppSelector } from 'redux/hooks';
import { getUserPermissions } from 'redux/slices/userData';

/**
 * Zone codes that suppress all price information.
 * When the selected commercial zone matches one of these codes,
 * no prices are shown and no price list lookups are triggered.
 */
const PRICE_SUPPRESSED_ZONES = ['*I', '*E', '*T'];

/**
 * Returns `true` when prices should be visible.
 *
 * Prices are hidden when:
 * - The user lacks the `VIEW_PRICE` permission, OR
 * - The currently selected commercial zone is `*I`, `*E`, or `*T`.
 */
const usePriceVisible = (): boolean => {
  const zone = useAppSelector((state) => state.settings.zone);
  const permissions = useAppSelector(getUserPermissions);

  const hasPermission = permissions.includes('VIEW_PRICE');
  const isZoneSuppressed = PRICE_SUPPRESSED_ZONES.includes(zone);

  return hasPermission && !isZoneSuppressed;
};

/**
 * Non-hook utility for contexts where React hooks cannot be called.
 * Accepts the zone code directly and returns whether prices are suppressed.
 */
const isPriceZoneSuppressed = (zoneCode: string): boolean =>
  PRICE_SUPPRESSED_ZONES.includes(zoneCode);

export { usePriceVisible, isPriceZoneSuppressed, PRICE_SUPPRESSED_ZONES };
